import React, { useState, useEffect, useCallback } from 'react';
import ImageUploader from './components/ImageUploader';
import ImageList from './components/ImageList';
import SettingsPanel from './components/SettingsPanel';
import PreviewStrip from './components/PreviewStrip';
import { processFile } from './utils/imageProcessor';
import { generatePDF } from './utils/pdfGenerator';
import { exportPagesAsZip } from './utils/pageExporter';
import { DEFAULT_SETTINGS } from './utils/constants';
import SafeIcon from './common/SafeIcon';
import {
  FiDownload,
  FiLoader,
  FiAlertCircle,
  FiImage,
} from 'react-icons/fi';
import './App.css';

function App() {
  const [images, setImages] = useState([]);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('pdfSettings');
    return saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    localStorage.setItem('pdfSettings', JSON.stringify(settings));
  }, [settings]);

  const handleUpload = useCallback(async (files) => {
    setIsProcessing(true);
    setError('');

    try {
      const processed = [];

      for (const file of files) {
        try {
          processed.push(await processFile(file));
        } catch (err) {
          console.error('Failed to process', file.name, err);
        }
      }

      if (processed.length === 0 && files.length > 0) {
        setError('No valid images were found in the upload.');
      } else if (processed.length < files.length) {
        setError(
          `Successfully loaded ${processed.length} images. ${
            files.length - processed.length
          } files were invalid.`
        );
      }

      setImages((previous) => [...previous, ...processed]);
    } catch (err) {
      setError('An unexpected error occurred while reading the files.');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const removeImage = (id) => {
    setImages((previous) => previous.filter((image) => image.id !== id));
  };

  const clearAllImages = () => {
    if (window.confirm('Are you sure you want to remove all uploaded images?')) {
      setImages([]);
    }
  };

  const handleGenerate = async () => {
    if (images.length === 0) return;

    setIsProcessing(true);
    setProgress(1);

    try {
      setTimeout(async () => {
        await generatePDF(images, settings, setProgress);
        setIsProcessing(false);
        setProgress(0);
      }, 100);
    } catch (err) {
      setError('Failed to generate PDF. The images might be too large.');
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handlePageExport = async (format) => {
    if (images.length === 0) return;

    setIsProcessing(true);
    setProgress(1);
    setError('');

    try {
      await exportPagesAsZip(images, settings, format, setProgress);
    } catch (err) {
      setError('Failed to create the image ZIP archive.');
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex flex-col items-center mb-10">
          <div className="flex items-center gap-5 mb-3">
            <img
              src="https://media-manager-c.questera.ai/greta-media/981ecb6892631c31e92fca6d10d0332603596e5d429fd99cee63046a83be378b4365dffb7696bc6c52f453f1ea7f8977/images/aW1hZ2UvcG5n/5f0c439dbb370c6764bb0f46103aac49.png"
              alt="PicFolio Toucan Logo"
              className="h-20 w-20 object-contain rounded-2xl shadow-sm bg-white p-1"
            />
            <h1 className="text-5xl md:text-6xl font-black text-gray-900 tracking-tight">
              PicFolio Toucan
            </h1>
          </div>
          <p className="text-xl text-gray-500 font-medium text-center max-w-2xl">
            The ultimate browser-based toolkit for creating high-quality PDF
            portfolios from your images.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-xl flex items-start gap-4 shadow-sm">
            <SafeIcon icon={FiAlertCircle} className="text-red-500 mt-0.5 text-2xl" />
            <div className="flex-1">
              <p className="text-lg font-bold text-red-800">Something went wrong</p>
              <p className="text-base text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <SafeIcon icon={FiAlertCircle} className="rotate-45 text-xl" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm">
              <ImageUploader onUpload={handleUpload} />
              {isProcessing && progress === 0 && (
                <div className="mt-8 flex items-center justify-center p-6 bg-blue-50 rounded-2xl border border-blue-100 animate-pulse">
                  <SafeIcon icon={FiLoader} className="animate-spin text-blue-600 mr-4 text-xl" />
                  <span className="text-base font-bold text-blue-700">
                    Processing images...
                  </span>
                </div>
              )}
              <ImageList
                images={images}
                setImages={setImages}
                onRemove={removeImage}
                onClearAll={clearAllImages}
              />
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col gap-8">
            <SettingsPanel settings={settings} setSettings={setSettings} />
            <PreviewStrip images={images} settings={settings} />

            <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-lg flex flex-col gap-6 sticky bottom-8 z-20">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">
                    Status
                  </span>
                  <div className="text-lg font-bold text-gray-800">
                    {images.length > 0 ? (
                      <span>
                        {images.length} image{images.length !== 1 ? 's' : ''} ready
                      </span>
                    ) : (
                      <span className="text-gray-400">Waiting for uploads</span>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={images.length === 0 || isProcessing}
                  className={`group flex items-center gap-4 px-10 py-5 rounded-2xl font-black uppercase tracking-widest text-base transition-all ${
                    images.length === 0 || isProcessing
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl hover:-translate-y-0.5'
                  }`}
                >
                  {isProcessing && progress > 0 ? (
                    <>
                      <SafeIcon icon={FiLoader} className="animate-spin text-xl" />
                      Rendering {progress}%
                    </>
                  ) : (
                    <>
                      <SafeIcon
                        icon={FiDownload}
                        className="text-xl group-hover:animate-bounce"
                      />
                      Download PDF
                    </>
                  )}
                </button>
              </div>

              <div className="border-t border-gray-100 pt-5">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">
                  Download pages as images
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => handlePageExport('jpg')}
                    disabled={images.length === 0 || isProcessing}
                    className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-gray-200 text-sm font-black text-gray-700 hover:border-blue-400 hover:text-blue-600 transition-colors disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed"
                  >
                    <SafeIcon icon={FiImage} />
                    JPG Pages ZIP
                  </button>
                  <button
                    onClick={() => handlePageExport('png')}
                    disabled={images.length === 0 || isProcessing}
                    className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-gray-200 text-sm font-black text-gray-700 hover:border-blue-400 hover:text-blue-600 transition-colors disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed"
                  >
                    <SafeIcon icon={FiImage} />
                    PNG Pages ZIP
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;